/*

  GataDagsins.ts

  Gáta Dagsins main view component

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { ComponentFunc, m } from "./mithril";
import { IPlayerMove, IRiddle, IView } from "./types";
import { GataDagsinsBoardAndRack } from "./gatadagsins-board-rack";
import { GataDagsinsRightSide } from "./gatadagsins-right-side";
import { BlankDialog } from "./blankdialog";
import { GataDagsinsHelp } from "./gatadagsins-help";
import { StatsModal } from "./statsmodal";
import { Beginner } from "./beginner";
import { glyph } from "./util";
import { ts } from "./i18n";

interface IAttributes {
  view: IView;
  date: string; // The date for the riddle, in ISO format
  locale: string; // The locale for the riddle, e.g., "is_IS"
}

const MAX_MOVES_TO_DISPLAY = 10;

const currentMoveState = (riddle: IRiddle) => {
  const thisPlayer = riddle.model.state?.netskraflUserId || "";
  const {
    bestPossibleScore, // The highest score achievable for this riddle
    globalBestScore, // The best score achieved by any player
    groupBestScore, // The best score achieved within the player's group
    playerMoves,
  } = riddle;
  // If the player has equaled the best possible score,
  // the winning word is stored here and displayed at the top
  let bestMove: IPlayerMove | undefined = undefined;

  // Sort moves by score in descending order and
  // cut the tail off the list to only include the top moves
  const selectedMoves: IPlayerMove[] = playerMoves
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_MOVES_TO_DISPLAY)
    .map(move => ({
      score: move.score,
      word: move.word,
      coord: move.coord,
    }));

  // Check whether we need to add or annotate the global best score
  if (globalBestScore && globalBestScore.score > 0) {
    const { score, word, coord } = globalBestScore;
    if ((selectedMoves[0]?.score ?? 0) >= score) {
      // This player has made a move that scores the same
      // or better as the top score: mark the move
      selectedMoves[0].isGlobalBestScore = true;
    } else if (globalBestScore.player === thisPlayer) {
      // This player holds the global best score, probably
      // from a previous session, so it's not already
      // in the selectedMoves list: add it as a move
      selectedMoves.unshift({ score, isGlobalBestScore: true, word, coord });
    } else {
      // This is a global best score from another player
      selectedMoves.unshift({ score, isGlobalBestScore: true, word: "", coord: "" });
    }
  }

  // Check if the best possible score has been achieved, by this player
  // or another player. If so, we remove it from the move list, since we
  // only display it at the top of the thermometer.
  if (selectedMoves.length > 0 && selectedMoves[0].score === bestPossibleScore) {
      bestMove = selectedMoves.shift();
  }

  return { selectedMoves, bestMove };
};

export const GataDagsins: ComponentFunc<IAttributes> = () => {
  // A view of the Gáta Dagsins page
  let showHelp = false;
  let showStatsModal = false;
  return {
    oninit: async (vnode) => {
      const { model, actions } = vnode.attrs.view;
      const { riddle } = model;
      if (!riddle) {
        const { date, locale } = vnode.attrs;
        // Initialize a fresh riddle object if it doesn't exist
        await actions.fetchRiddle(date, locale);
      }
      // Initialize dialog states
      showHelp = false;
      showStatsModal = false;
    },
    view: (vnode) => {
      const { view } = vnode.attrs;
      const { model } = view;
      const { riddle } = model;
      const { selectedMoves, bestMove } = (
        riddle
        ? currentMoveState(riddle)
        : { selectedMoves: [], bestMove: undefined }
      );
      const toggleHelp = () => {
        showHelp = !showHelp;
        m.redraw();
      };
      const toggleStatsModal = () => {
        showStatsModal = !showStatsModal;
        m.redraw();
      };

      return m(
        "div.drop-target",
        {
          id: "gatadagsins-background",
        },
        [
          // The main content area
          m(".gatadagsins-container",
            [
              // Main display area with flex layout
              riddle ? m(".gatadagsins-main",
                [
                  // Board and rack component (left side)
                  m(GataDagsinsBoardAndRack, { view }),
                  // Right-side component with scores and comparisons
                  m(GataDagsinsRightSide, { view, selectedMoves, bestMove, onStatsClick: toggleStatsModal }),
                  // Blank dialog
                  riddle.askingForBlank
                    ? m(BlankDialog, { game: riddle })
                    : "",
                ]
              ) : "",
              // The left margin elements: back button and info/help button
              // These elements appear after the main container for proper z-order
              // m(LeftLogo), // Currently no need for the logo for Gáta Dagsins
              // Show the Beginner component if the user is a beginner
              model.state?.beginner ? m(Beginner, { view, showClose: false }) : "",
              // Custom Info button for GataDagsins that shows help dialog
              m(".info",
                { title: ts("Upplýsingar og hjálp") },
                m("a.iconlink",
                  { href: "#", onclick: (e: Event) => { e.preventDefault(); toggleHelp(); }},
                  glyph("info-sign")
                )
              ),
              // Help dialog and backdrop
              showHelp ? m(GataDagsinsHelp, { onClose: toggleHelp }) : "",
            ]
          ),
          // Stats modal and backdrop (mobile only)
          showStatsModal ? m(StatsModal, { view, onClose: toggleStatsModal }) : "",
        ],
      );
    }
  };
};
